import React, { useEffect, useState } from "react";
import { Card, Button, Space, Grid, message, notification } from "antd";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { useMemberService } from "../../../../services/members/MemberServiceProvider";
import MemberPhoto from "../../../member/MemberPhoto";
import PhotoCapture from "../../PhotoCapture";
import { MemberProfileMode } from "./modeConfig";
import { statusMeta } from "./validity";

interface MemberPhotoCardProps {
    member: MemberProfile;
    mode: MemberProfileMode;
    onPhotoUploaded: () => void;
}

const ringColor = (tone: "ok" | "warn" | "err"): string =>
    tone === "ok" ? "var(--ft-success)" : tone === "warn" ? "var(--ft-warning)" : "var(--ft-error)";

// The member photo as its own left-rail card — separated from the identity row
// so a large photo doesn't stretch the header. A status-coloured ring conveys
// state at a glance; staff get the capture/upload controls (gated on photoUpload).
const MemberPhotoCard: React.FC<MemberPhotoCardProps> = ({ member, mode, onPhotoUploaded }) => {
    const screens = Grid.useBreakpoint();
    const compact = screens.lg === false;
    const memberService = useMemberService();
    const sm = statusMeta(member);

    const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handlePhotoChange = (file: File | null) => {
        setSelectedPhoto(file);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(file ? URL.createObjectURL(file) : null);
    };

    const handlePhotoUpload = async () => {
        if (!selectedPhoto) {
            message.warning("Please select a photo first");
            return;
        }
        setUploading(true);
        try {
            await memberService.uploadMemberPhoto(member.membershipId, selectedPhoto);
            message.success("Photo uploaded successfully");
            handlePhotoChange(null);
            onPhotoUploaded();
        } catch (error) {
            console.error("Error uploading photo:", error);
            notification.error({ message: "Error", description: "Failed to upload photo. Please try again." });
        } finally {
            setUploading(false);
        }
    };

    const photoSize = compact ? 160 : 200;

    return (
        <Card size="small" styles={{ body: { padding: 16 } }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                <div style={{ padding: 4, border: `3px solid ${ringColor(sm.tone)}`, borderRadius: "50%", lineHeight: 0 }}>
                    {previewUrl ? (
                        <img
                            src={previewUrl}
                            alt="Preview"
                            style={{ width: photoSize, height: photoSize, borderRadius: "50%", objectFit: "cover", display: "block" }}
                        />
                    ) : (
                        <MemberPhoto memberProfile={member} size={photoSize} />
                    )}
                </div>
                {mode.photoUpload && (
                    <PhotoCapture onChange={handlePhotoChange} disabled={uploading} hidePlaceholder showPreview={false} />
                )}
                {selectedPhoto && mode.photoUpload && (
                    <Space size={6}>
                        <Button size="small" type="primary" loading={uploading} onClick={handlePhotoUpload}>
                            Upload
                        </Button>
                        <Button size="small" disabled={uploading} onClick={() => handlePhotoChange(null)}>
                            Cancel
                        </Button>
                    </Space>
                )}
            </div>
        </Card>
    );
};

export default MemberPhotoCard;
